import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { strapiFetch } from "../utils/strapi";

interface User {
  id: number;
  username: string;
  email: string;
}

interface AuthContextType {
  // null = not yet determined (during SSR / before mount)
  isLoggedIn: boolean | null;
  user: User | null;
  login: (identifier: string, password: string) => Promise<void>;
  register: (
    username: string,
    email: string,
    password: string,
  ) => Promise<{ requiresEmailConfirmation: boolean }>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (
    code: string,
    password: string,
    passwordConfirmation: string,
  ) => Promise<void>;
  fetchUser: () => Promise<void>;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = "stl_parts_jwt";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // The global login state is derived from token presence only. User data
  // is fetched on demand by pages that need it (see fetchUser).
  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem(STORAGE_KEY));
  }, []);

  const getToken = useCallback(() => localStorage.getItem(STORAGE_KEY), []);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEY);
    if (!token) {
      setIsLoggedIn(false);
      setUser(null);
      return;
    }

    try {
      // /users/me returns the user object directly (not wrapped in `data`).
      const userData = await strapiFetch("/api/users/me", {
        token,
        unwrap: false,
      });
      setUser(userData);
    } catch (error) {
      // An invalid/expired token throws here; clear it and self-heal to
      // logged-out.
      console.error("Fetching user failed:", error);
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
      setIsLoggedIn(false);
    }
  }, []);

  const login = async (identifier: string, password: string) => {
    const data = await strapiFetch("/api/auth/local", {
      method: "POST",
      body: { identifier, password },
      unwrap: false,
      errorFallback: "Login failed",
    });
    localStorage.setItem(STORAGE_KEY, data.jwt);
    setUser(data.user);
    setIsLoggedIn(true);
  };

  const register = async (
    username: string,
    email: string,
    password: string,
  ) => {
    const data = await strapiFetch("/api/auth/local/register", {
      method: "POST",
      body: { username, email, password },
      unwrap: false,
      errorFallback: "Registration failed",
    });

    // When email confirmation is enabled in Strapi, the response contains
    // the user but no JWT until the email address is confirmed.
    if (data.jwt) {
      localStorage.setItem(STORAGE_KEY, data.jwt);
      setUser(data.user);
      setIsLoggedIn(true);
    }

    return { requiresEmailConfirmation: !data.jwt };
  };

  // Strapi answers { ok: true } here whether or not an account with that email
  // exists (deliberately, to prevent user enumeration), so the UI can only
  // ever say "if an account exists, we sent a link".
  const forgotPassword = async (email: string) => {
    await strapiFetch("/api/auth/forgot-password", {
      method: "POST",
      body: { email },
      unwrap: false,
      errorFallback: "Sending the reset link failed",
    });
  };

  const resetPassword = async (
    code: string,
    password: string,
    passwordConfirmation: string,
  ) => {
    const data = await strapiFetch("/api/auth/reset-password", {
      method: "POST",
      body: { code, password, passwordConfirmation },
      unwrap: false,
      errorFallback: "Resetting the password failed",
    });

    // The response carries a fresh JWT, so a successful reset also logs the
    // user in (same shape as /auth/local).
    if (data.jwt) {
      localStorage.setItem(STORAGE_KEY, data.jwt);
      setUser(data.user);
      setIsLoggedIn(true);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setIsLoggedIn(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword,
        fetchUser,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
